// =============================================================================
// Menu slug helpers.
//
// The Menu table now carries a per-menu `slug` (URL-safe lowercase string).
// The frontend URL hierarchy is the slug joined with "/" from the root down
// — so a menu under "Products" > "Shukla Hip" called "Blade" becomes
// "products/shukla-hip/blade".
//
// These helpers operate over the menu tree the navbar fetches once at boot
// (GET /api/Common/Menu), keeping all the slug math client-side so we don't
// pay an API round-trip on every navigation. The server-side helpers in
// CommonRepository (`GetMenuBySlugPath` / `GetSlugPathForMenu`) are the
// authoritative copy and back the API endpoints, but mirroring the logic
// here lets navigate() return a slug URL synchronously.
// =============================================================================

// Build a flat slug path from a menuId by walking parents up to the root.
// Returns null if any ancestor lacks a slug — caller should fall back to the
// numeric route in that case so navigation never silently breaks.
export function buildSlugPath(menuData, menuId) {
  if (!Array.isArray(menuData) || menuId == null) return null;
  const id = Number(menuId);
  if (!Number.isFinite(id)) return null;

  const byId = new Map(menuData.map((m) => [Number(m.menuId), m]));
  const segments = [];
  let cursor = byId.get(id);
  // Cycle guard — should never trip on real data, but cheap to add.
  const seen = new Set();
  while (cursor) {
    if (seen.has(cursor.menuId)) return null;
    seen.add(cursor.menuId);
    if (!cursor.slug) return null;
    segments.push(String(cursor.slug));
    const parentId = Number(cursor.parentMenuId);
    if (!parentId || parentId === 0) break;
    cursor = byId.get(parentId);
  }
  segments.reverse();
  return segments.join('/');
}

// Reverse — given a slug path, return the menu object at that path. Walks
// segment-by-segment scoped to the previous segment's id so duplicate leaf
// slugs (e.g. "blade" under both hip and knee) resolve correctly.
export function findMenuBySlugPath(menuData, slugPath) {
  if (!Array.isArray(menuData) || !slugPath) return null;
  const segments = String(slugPath)
    .split('/')
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);
  if (segments.length === 0) return null;

  let parentId = 0;
  let current = null;
  for (const segment of segments) {
    // Capture parentId in a per-iteration const so the find() callback doesn't
    // close over the loop-mutated `let` (avoids the no-loop-func lint warning).
    const scopeParentId = parentId;
    current = menuData.find(
      (m) =>
        Number(m.parentMenuId) === scopeParentId &&
        m.isActive &&
        (m.slug || '').toLowerCase() === segment
    );
    if (!current) return null;
    parentId = Number(current.menuId);
  }
  return current;
}

// Flat-slug lookup, ignoring parent. Used by /:moduleSlug/{search|create|edit}
// where the URL carries only the menu's own slug (e.g. "sales-territory")
// rather than the full ancestor path. If two menus share a slug under
// different parents (theoretically possible — the unique index is per-parent
// only), returns the first active match by table order.
export function findMenuBySlug(menuData, slug) {
  if (!Array.isArray(menuData) || !slug) return null;
  const target = String(slug).trim().toLowerCase();
  if (!target) return null;
  return (
    menuData.find(
      (m) =>
        m.isActive &&
        (m.slug || '').toLowerCase() === target
    ) || null
  );
}

// Reverse of findMenuBySlug — given a menuId, return that menu's own slug.
// Returned slug is suitable for the flat /:moduleSlug/{action} grammar.
// Falls back to null when the menu or slug is missing so callers can decide
// whether to emit the numeric id instead.
export function getLeafSlug(menuData, menuId) {
  if (!Array.isArray(menuData) || menuId == null) return null;
  const id = Number(menuId);
  if (!Number.isFinite(id)) return null;
  const menu = menuData.find((m) => Number(m.menuId) === id);
  return menu?.slug || null;
}
